/**
 * Vent Renderer - Draws supply registers and return grilles on canvas
 */

import {
  VENT_TYPES,
  SUPPLY_REGISTER_SIZES,
  RETURN_GRILLE_SIZES,
} from '../../constants/hvacEquipment';

/**
 * Draw a single vent (supply register or return grille)
 * Canvas context should already have scale transform applied
 * All coordinates are in world space (inches)
 */
export function drawVent(ctx, vent, scale, isSelected = false, isPreview = false) {
  const ventType = VENT_TYPES[vent.type] || VENT_TYPES['supply-floor'];
  const isSupply = ventType.category === 'supply';

  // Get size dimensions
  const sizes = isSupply ? SUPPLY_REGISTER_SIZES : RETURN_GRILLE_SIZES;
  const sizeObj = sizes.find((s) => s.value === vent.size) || sizes[0];
  const width = sizeObj.width;
  const height = sizeObj.height;

  ctx.save();

  // Move to vent position and apply rotation
  ctx.translate(vent.x, vent.y);
  ctx.rotate((vent.rotation || 0) * Math.PI / 180);

  // Set styles
  const fillOpacity = isPreview ? 0.3 : 0.6;
  ctx.fillStyle = isSelected
    ? `rgba(0, 200, 255, ${fillOpacity})`
    : `${ventType.color}${Math.round(fillOpacity * 255).toString(16).padStart(2, '0')}`;
  ctx.strokeStyle = isSelected ? '#00c8ff' : ventType.strokeColor;
  ctx.lineWidth = (isSelected ? 2 : 1) / scale;

  // Draw vent centered at origin
  const halfW = width / 2;
  const halfH = height / 2;

  // Main rectangle
  ctx.beginPath();
  ctx.rect(-halfW, -halfH, width, height);
  ctx.fill();
  ctx.stroke();

  // Draw pattern based on type
  if (isSupply) {
    // Supply register - horizontal slats
    drawSupplyPattern(ctx, width, height, ventType.strokeColor, scale);
  } else {
    // Return grille - grid pattern
    drawReturnPattern(ctx, width, height, ventType.strokeColor, scale, vent.type.includes('filter'));
  }

  // Draw label
  if (!isPreview) {
    drawVentLabel(ctx, vent, width, height, scale);
  }

  // Draw selection handles if selected
  if (isSelected) {
    drawVentHandles(ctx, width, height, scale);
  }

  ctx.restore();
}

/**
 * Draw supply register pattern (horizontal slats)
 */
function drawSupplyPattern(ctx, width, height, color, scale) {
  const halfW = width / 2;
  const halfH = height / 2;
  const numSlats = Math.max(3, Math.floor(height / 3));
  const spacing = height / (numSlats + 1);

  ctx.strokeStyle = color;
  ctx.lineWidth = 0.5 / scale;

  for (let i = 1; i <= numSlats; i++) {
    const y = -halfH + spacing * i;
    ctx.beginPath();
    ctx.moveTo(-halfW + 1, y);
    ctx.lineTo(halfW - 1, y);
    ctx.stroke();
  }

  // Draw airflow direction arrows
  ctx.fillStyle = color;
  ctx.globalAlpha = 0.4;
  const arrowY = 0;
  ctx.beginPath();
  ctx.moveTo(0, arrowY - 2);
  ctx.lineTo(-2, arrowY + 2);
  ctx.lineTo(2, arrowY + 2);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
}

/**
 * Draw return grille pattern (grid)
 */
function drawReturnPattern(ctx, width, height, color, scale, isFilter) {
  const halfW = width / 2;
  const halfH = height / 2;
  const gridSize = 4; // 4 inch grid

  ctx.strokeStyle = color;
  ctx.lineWidth = 0.5 / scale;

  // Vertical lines
  for (let x = -halfW + gridSize; x < halfW; x += gridSize) {
    ctx.beginPath();
    ctx.moveTo(x, -halfH + 1);
    ctx.lineTo(x, halfH - 1);
    ctx.stroke();
  }

  // Horizontal lines
  for (let y = -halfH + gridSize; y < halfH; y += gridSize) {
    ctx.beginPath();
    ctx.moveTo(-halfW + 1, y);
    ctx.lineTo(halfW - 1, y);
    ctx.stroke();
  }

  // Filter indicator
  if (isFilter) {
    ctx.setLineDash([2 / scale, 2 / scale]);
    ctx.lineWidth = 1.5 / scale;
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.rect(-halfW + 2, -halfH + 2, width - 4, height - 4);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

/**
 * Draw vent label (size and CFM)
 */
function drawVentLabel(ctx, vent, width, height, scale) {
  const label = vent.size;
  const cfmLabel = vent.cfm ? `${vent.cfm} CFM` : '';

  const fontSize = Math.min(10, Math.max(6, width / 4)) / scale;
  ctx.font = `bold ${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  // Background
  const textWidth = Math.max(ctx.measureText(label).width, ctx.measureText(cfmLabel).width) + 4 / scale;
  const textHeight = (cfmLabel ? fontSize * 2.2 : fontSize) + 4 / scale;

  ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
  ctx.fillRect(-textWidth / 2, -textHeight / 2, textWidth, textHeight);

  // Text
  ctx.fillStyle = '#fff';
  if (cfmLabel) {
    ctx.fillText(label, 0, -fontSize * 0.4);
    ctx.font = `${fontSize * 0.8}px Arial`;
    ctx.fillText(cfmLabel, 0, fontSize * 0.5);
  } else {
    ctx.fillText(label, 0, 0);
  }
}

/**
 * Draw selection handles for vent
 */
function drawVentHandles(ctx, width, height, scale) {
  const halfW = width / 2;
  const halfH = height / 2;
  const handleSize = 4 / scale;

  ctx.fillStyle = '#00c8ff';
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 1 / scale;

  // Corner handles
  const corners = [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH],
  ];

  corners.forEach(([x, y]) => {
    ctx.beginPath();
    ctx.rect(x - handleSize / 2, y - handleSize / 2, handleSize, handleSize);
    ctx.fill();
    ctx.stroke();
  });

  // Rotation handle
  ctx.beginPath();
  ctx.arc(0, -halfH - 10 / scale, handleSize / 2, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();

  // Line to rotation handle
  ctx.beginPath();
  ctx.moveTo(0, -halfH);
  ctx.lineTo(0, -halfH - 10 / scale + handleSize / 2);
  ctx.stroke();
}

/**
 * Draw all vents
 */
export function drawAllVents(ctx, vents, scale, selectedVentId = null) {
  if (!vents || vents.length === 0) return;

  // Draw non-selected vents first
  vents.forEach((vent) => {
    if (vent.id !== selectedVentId) {
      drawVent(ctx, vent, scale, false, false);
    }
  });

  // Draw selected vent on top
  const selectedVent = vents.find((v) => v.id === selectedVentId);
  if (selectedVent) {
    drawVent(ctx, selectedVent, scale, true, false);
  }
}

/**
 * Draw vent preview while placing
 */
export function drawVentPreview(ctx, x, y, ventType, ventSize, rotation, scale) {
  const previewVent = {
    x,
    y,
    type: ventType,
    size: ventSize,
    rotation: rotation || 0,
  };

  drawVent(ctx, previewVent, scale, false, true);
}

/**
 * Hit test for vent selection
 * @param {number} worldX - World X coordinate (inches)
 * @param {number} worldY - World Y coordinate (inches)
 * @param {Object} vent - Vent object
 * @param {number} scale - Current zoom scale
 */
export function hitTestVent(worldX, worldY, vent, scale) {
  const ventType = VENT_TYPES[vent.type] || VENT_TYPES['supply-floor'];
  const isSupply = ventType.category === 'supply';
  const sizes = isSupply ? SUPPLY_REGISTER_SIZES : RETURN_GRILLE_SIZES;
  const sizeObj = sizes.find((s) => s.value === vent.size) || sizes[0];

  const width = sizeObj.width;
  const height = sizeObj.height;

  // Transform click point to vent's local coordinates
  const dx = worldX - vent.x;
  const dy = worldY - vent.y;
  const rotation = (vent.rotation || 0) * Math.PI / 180;

  // Rotate point back to align with vent axes
  const localX = dx * Math.cos(-rotation) - dy * Math.sin(-rotation);
  const localY = dx * Math.sin(-rotation) + dy * Math.cos(-rotation);

  // Check if point is within vent bounds (with padding)
  const padding = Math.max(5, 5 / scale);
  const halfW = width / 2 + padding;
  const halfH = height / 2 + padding;

  return Math.abs(localX) <= halfW && Math.abs(localY) <= halfH;
}

export default {
  drawVent,
  drawAllVents,
  drawVentPreview,
  hitTestVent,
};
